(() => {
  if (window.__imageGalleryPeekBound) return;
  window.__imageGalleryPeekBound = true;

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const easeOutCubic = (t) => 1 - (1 - t) ** 3;
  const easeOutQuad = (t) => 1 - (1 - t) * (1 - t);

  const initPeekGallery = (root) => {
    if (!root || root.hasAttribute('data-gallery-peek-init')) return;
    if (!root.classList.contains('image-gallery--multiple')) return;
    if (!root.classList.contains('image-gallery--peek')) return;
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    const viewport = root.querySelector('.image-gallery__viewport');
    const track = root.querySelector('[data-gallery-track]');
    const slides = Array.from(root.querySelectorAll('[data-gallery-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-gallery-dot]'));
    const prev = root.querySelector('[data-gallery-prev]');
    const next = root.querySelector('[data-gallery-next]');
    if (!viewport || !track || slides.length < 2) return;

    root.setAttribute('data-gallery-peek-init', '');

    const showPeekAtStart = root.classList.contains('image-gallery--peek-start');
    let index = Number(root.dataset.galleryIndex || 0) || 0;
    index = ((index % slides.length) + slides.length) % slides.length;
    let dragOffset = 0;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let lockAxis = null;
    let frame = 0;
    let width = 0;
    let step = 0;
    let sideInset = 0;
    let isPointerActive = false;
    const desktopMq = window.matchMedia('(min-width: 750px)');

    const isRtl = () =>
      root.classList.contains('image-gallery--dir-rtl') ||
      (root.classList.contains('image-gallery--dir-auto') &&
        (document.body?.getAttribute('data-rtl') === 'true' ||
          document.documentElement.getAttribute('dir') === 'rtl')) ||
      getComputedStyle(root).direction === 'rtl';

    const dir = () => (isRtl() ? -1 : 1);

    const readCssNumber = (name, fallback) => {
      const value = Number.parseFloat(getComputedStyle(root).getPropertyValue(name));
      return Number.isFinite(value) ? value : fallback;
    };

    const resetInlineLayout = () => {
      window.cancelAnimationFrame(frame);
      pointerId = null;
      isPointerActive = false;
      lockAxis = null;
      dragOffset = 0;
      frame = 0;
      viewport.style.height = '';
      track.style.height = '';
      track.style.transform = '';
      root.classList.remove('is-dragging', 'is-interactive');
      root.removeAttribute('data-gallery-peek-ready');
      slides.forEach((slide) => {
        slide.style.width = '';
        slide.style.height = '';
        slide.style.transform = '';
        slide.style.opacity = '';
        slide.style.visibility = '';
        slide.style.zIndex = '';
      });
    };

    const metrics = () => {
      const rootWidth = viewport.clientWidth || root.clientWidth || 1;
      const gap = readCssNumber('--ig-peek-gap', 20);
      const peekShow = readCssNumber('--ig-peek-show', 56);
      const aspectW = readCssNumber('--ig-aspect-w', 4);
      const aspectH = readCssNumber('--ig-aspect-h', 3);
      sideInset = showPeekAtStart ? peekShow + gap : 0;
      width = Math.max(180, rootWidth - peekShow - gap);
      step = width + gap;
      const height = width * (aspectH / aspectW);

      viewport.style.height = `${height}px`;
      track.style.height = `${height}px`;
      slides.forEach((slide) => {
        slide.style.width = `${width}px`;
        slide.style.height = `${height}px`;
      });
    };

    const setActive = (nextIndex) => {
      index = ((nextIndex % slides.length) + slides.length) % slides.length;
      root.dataset.galleryIndex = String(index);

      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });
    };

    const paint = (slide, x, scale, opacity, z) => {
      slide.style.transform = `translate3d(${x * dir()}px, 0, 0) scaleY(${scale})`;
      slide.style.opacity = String(opacity);
      slide.style.visibility = opacity > 0.03 ? 'visible' : 'hidden';
      slide.style.zIndex = String(z);
    };

    const render = (offset = 0) => {
      metrics();
      const nextIndex = (index + 1) % slides.length;
      const prevIndex = (index - 1 + slides.length) % slides.length;
      const hasSharedNeighbor = slides.length === 2 && nextIndex === prevIndex;
      const towardNext = clamp(-offset / step, 0, 1);
      const towardPrev = clamp(offset / step, 0, 1);
      const travel = Math.max(towardNext, towardPrev);
      const quickTravel = easeOutQuad(clamp(travel * 1.35, 0, 1));
      const sideScale = readCssNumber('--ig-peek-scale', 0.82);
      const restOpacity = readCssNumber('--ig-peek-opacity', 0.28);

      slides.forEach((slide, i) => {
        if (i === index) {
          const activeScale = 1 - (1 - sideScale) * quickTravel;
          const activeOpacity = 1 - (1 - restOpacity) * quickTravel;
          paint(slide, sideInset + offset, activeScale, activeOpacity, 3);
          return;
        }

        if (hasSharedNeighbor && i === prevIndex && towardPrev > towardNext) {
          const incoming = easeOutQuad(clamp(towardPrev * 1.25, 0, 1));
          const scale = sideScale + (1 - sideScale) * incoming;
          const opacity = restOpacity + (1 - restOpacity) * incoming;
          paint(slide, sideInset - step + offset, scale, opacity, 2);
          return;
        }

        if (i === nextIndex && (!showPeekAtStart || towardNext > 0)) {
          const incoming = easeOutQuad(clamp(towardNext * 1.25, 0, 1));
          const scale = sideScale + (1 - sideScale) * incoming;
          const opacity = restOpacity + (1 - restOpacity) * incoming;
          paint(slide, sideInset + step + offset, scale, opacity, 2);
          return;
        }

        if (i === prevIndex && showPeekAtStart && towardPrev === 0 && towardNext === 0) {
          paint(slide, sideInset - step, sideScale, restOpacity, 2);
          return;
        }

        if (i === prevIndex && towardPrev > 0) {
          const incoming = easeOutQuad(clamp(towardPrev * 1.25, 0, 1));
          const scale = sideScale + (1 - sideScale) * incoming;
          const opacity = restOpacity + (1 - restOpacity) * incoming;
          paint(slide, sideInset - step + offset, scale, opacity, 2);
          return;
        }

        paint(slide, showPeekAtStart ? step * 2 : step, sideScale, 0, 1);
      });
    };

    const animateTo = (to, done) => {
      window.cancelAnimationFrame(frame);
      const from = dragOffset;
      const start = performance.now();
      const duration = 95;
      root.classList.add('is-dragging');

      const tick = (now) => {
        const t = clamp((now - start) / duration, 0, 1);
        dragOffset = from + (to - from) * easeOutCubic(t);
        render(dragOffset);

        if (t < 1) {
          frame = window.requestAnimationFrame(tick);
          return;
        }

        root.classList.remove('is-dragging');
        frame = 0;
        done?.();
      };

      frame = window.requestAnimationFrame(tick);
    };

    const goTo = (nextIndex, { smooth = true } = {}) => {
      const bounded = ((nextIndex % slides.length) + slides.length) % slides.length;
      if (!smooth || bounded === index) {
        dragOffset = 0;
        setActive(bounded);
        render(0);
        return;
      }

      const forward = (bounded - index + slides.length) % slides.length;
      const direction =
        slides.length === 2
          ? showPeekAtStart
            ? -1
            : 1
          : forward === 1
            ? 1
            : forward === slides.length - 1
              ? -1
              : 0;
      if (!direction) {
        dragOffset = 0;
        setActive(bounded);
        render(0);
        return;
      }

      animateTo(direction > 0 ? -step : step, () => {
        dragOffset = 0;
        setActive(bounded);
        render(0);
      });
    };

    const settle = () => {
      const threshold = step * 0.18;
      if (dragOffset <= -threshold) {
        goTo(index + 1);
        return;
      }
      if (dragOffset >= threshold) {
        goTo(index - 1);
        return;
      }
      animateTo(0, () => {
        dragOffset = 0;
        render(0);
      });
    };

    const onPointerDown = (event) => {
      if (!desktopMq.matches) return;
      if (event.button != null && event.button !== 0) return;
      window.cancelAnimationFrame(frame);
      pointerId = event.pointerId;
      isPointerActive = true;
      startX = event.clientX;
      startY = event.clientY;
      lockAxis = null;
      root.classList.add('is-dragging');
      try {
        track.setPointerCapture?.(pointerId);
      } catch (error) {}
    };

    const onPointerMove = (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = (event.clientX - startX) * dir();
      const dy = event.clientY - startY;

      if (!lockAxis) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        lockAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }

      if (lockAxis !== 'x') return;
      event.preventDefault();
      if (slides.length === 2) {
        dragOffset = (showPeekAtStart ? 1 : -1) * Math.abs(clamp(dx, -step, step));
      } else {
        dragOffset = clamp(dx, -step, step);
      }
      render(dragOffset);
    };

    const onPointerUp = (event) => {
      if (!isPointerActive && pointerId == null) return;
      if (pointerId != null && event.pointerId != null && event.pointerId !== pointerId) return;
      pointerId = null;
      isPointerActive = false;
      lockAxis = null;
      settle();
    };

    const finishInterruptedDrag = () => {
      if (!isPointerActive) return;
      pointerId = null;
      isPointerActive = false;
      lockAxis = null;
      settle();
    };

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('lostpointercapture', finishInterruptedDrag);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', finishInterruptedDrag);
    window.addEventListener('blur', finishInterruptedDrag);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') finishInterruptedDrag();
    });
    prev?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => goTo(Number(dot.dataset.galleryDot || 0)));
    });

    window.addEventListener('resize', () => {
      if (!desktopMq.matches) {
        resetInlineLayout();
        return;
      }
      dragOffset = 0;
      render(0);
      root.classList.add('is-interactive');
      root.setAttribute('data-gallery-peek-ready', '');
    });
    setActive(index);
    render(0);
    root.classList.add('is-interactive');
    root.setAttribute('data-gallery-peek-ready', '');
  };

  const run = () => {
    document
      .querySelectorAll('[data-image-gallery].image-gallery--peek.image-gallery--multiple')
      .forEach(initPeekGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', run);
  window.addEventListener('resize', run);
})();
